// Exercise name matching utilities for AI weight recommendations

// Common prefixes to strip for matching
const STRIP_PREFIXES = [
  'barbell', 'dumbbell', 'db', 'bb', 'cable', 'machine', 
  'seated', 'standing', 'incline', 'decline', 'flat',
  'single arm', 'single-arm', 'one arm', 'one-arm',
  'ez bar', 'ez-bar', 'smith machine', 'smith'
];

// Aliases mapping common variations to canonical names
const EXERCISE_ALIASES: Record<string, string[]> = {
  'bench press': ['bench', 'chest press', 'benchpress', 'benching', 'flat bench'],
  'squat': ['squats', 'back squat', 'back squats', 'front squat', 'front squats', 'squatting'],
  'deadlift': ['deadlifts', 'conventional deadlift', 'sumo deadlift', 'romanian deadlift', 'rdl'],
  'overhead press': ['ohp', 'shoulder press', 'military press', 'press'],
  'row': ['rows', 'bent over row', 'bent row', 'rowing', 'barbell row', 'dumbbell row'],
  'lat pulldown': ['pulldown', 'pull down', 'lat pull down', 'pulldowns'],
  'pull up': ['pullup', 'pullups', 'pull ups', 'chin up', 'chinup', 'chin ups'],
  'bicep curl': ['bicep curls', 'curl', 'curls', 'arm curl', 'arm curls'],
  'tricep extension': ['tricep extensions', 'skull crusher', 'skull crushers', 'tricep pushdown'],
  'leg press': ['leg presses', 'legpress'],
  'leg curl': ['leg curls', 'hamstring curl', 'hamstring curls'],
  'leg extension': ['leg extensions', 'quad extension', 'quad extensions'],
  'calf raise': ['calf raises', 'calf press', 'standing calf raise', 'seated calf raise'],
  'lateral raise': ['lateral raises', 'side raise', 'side raises', 'shoulder fly'],
  'hip thrust': ['hip thrusts', 'glute bridge', 'barbell hip thrust'],
  'lunge': ['lunges', 'walking lunge', 'walking lunges', 'reverse lunge'],
};

// Common "big lift" exercises that we should prompt users to add PRs for
export const COMMON_PR_EXERCISES = [
  'bench press', 'squat', 'deadlift', 'overhead press', 'row', 'pull up'
];

// Estimated starting weights by exercise, experience, and sex
// Format: { [exercise]: { [experience]: { male: weight, female: weight } } }
const ESTIMATED_WEIGHTS: Record<string, Record<string, { male: number; female: number }>> = {
  'bench press': {
    beginner: { male: 95, female: 45 },
    intermediate: { male: 135, female: 65 },
    advanced: { male: 185, female: 95 },
  },
  'squat': {
    beginner: { male: 95, female: 65 },
    intermediate: { male: 185, female: 95 },
    advanced: { male: 275, female: 135 },
  },
  'deadlift': {
    beginner: { male: 135, female: 95 },
    intermediate: { male: 225, female: 135 },
    advanced: { male: 315, female: 185 },
  },
  'overhead press': {
    beginner: { male: 65, female: 35 },
    intermediate: { male: 95, female: 55 },
    advanced: { male: 135, female: 75 },
  },
  'row': {
    beginner: { male: 65, female: 45 },
    intermediate: { male: 115, female: 65 },
    advanced: { male: 155, female: 95 },
  },
  'lat pulldown': {
    beginner: { male: 80, female: 50 },
    intermediate: { male: 120, female: 70 },
    advanced: { male: 160, female: 100 },
  },
  'bicep curl': {
    beginner: { male: 25, female: 15 },
    intermediate: { male: 40, female: 25 },
    advanced: { male: 55, female: 35 },
  },
  'tricep extension': {
    beginner: { male: 30, female: 20 },
    intermediate: { male: 50, female: 30 },
    advanced: { male: 70, female: 45 },
  },
  'leg press': {
    beginner: { male: 180, female: 90 },
    intermediate: { male: 270, female: 135 },
    advanced: { male: 360, female: 180 },
  },
  'leg curl': {
    beginner: { male: 50, female: 30 },
    intermediate: { male: 80, female: 50 },
    advanced: { male: 110, female: 70 },
  },
  'leg extension': {
    beginner: { male: 60, female: 40 },
    intermediate: { male: 100, female: 60 },
    advanced: { male: 140, female: 90 },
  },
  'lateral raise': {
    beginner: { male: 15, female: 8 },
    intermediate: { male: 25, female: 15 },
    advanced: { male: 35, female: 20 },
  },
  'hip thrust': {
    beginner: { male: 95, female: 65 },
    intermediate: { male: 185, female: 135 },
    advanced: { male: 275, female: 185 },
  },
  'lunge': {
    beginner: { male: 45, female: 25 },
    intermediate: { male: 95, female: 55 },
    advanced: { male: 135, female: 75 },
  },
  'calf raise': {
    beginner: { male: 90, female: 50 },
    intermediate: { male: 135, female: 80 },
    advanced: { male: 180, female: 110 },
  },
};

/**
 * Normalize an exercise name for matching
 * Strips prefixes, lowercases, and removes extra whitespace
 */
export function normalizeExerciseName(name: string): string {
  let normalized = name.toLowerCase().trim();
  
  // Strip common prefixes
  for (const prefix of STRIP_PREFIXES) {
    if (normalized.startsWith(prefix + ' ')) {
      normalized = normalized.slice(prefix.length + 1).trim();
    }
  }
  
  // Remove extra whitespace
  normalized = normalized.replace(/\s+/g, ' ');
  
  return normalized;
}

/**
 * Get the canonical name for an exercise (resolving aliases)
 */
export function getCanonicalName(name: string): string {
  const normalized = normalizeExerciseName(name);
  
  // Check if this matches any alias
  for (const [canonical, aliases] of Object.entries(EXERCISE_ALIASES)) {
    if (normalized === canonical || aliases.includes(normalized)) {
      return canonical;
    }
    // Also check if normalized name contains the canonical or any alias
    if (normalized.includes(canonical)) {
      return canonical;
    }
    for (const alias of aliases) {
      if (normalized.includes(alias) || alias.includes(normalized)) {
        return canonical;
      }
    }
  }
  
  return normalized;
}

/**
 * Check if two exercise names match (accounting for variations)
 */
export function exerciseNamesMatch(name1: string, name2: string): boolean {
  const canonical1 = getCanonicalName(name1);
  const canonical2 = getCanonicalName(name2);
  
  // Exact canonical match
  if (canonical1 === canonical2) {
    return true;
  }
  
  // Check if one contains the other (for partial matches)
  const norm1 = normalizeExerciseName(name1);
  const norm2 = normalizeExerciseName(name2);
  
  if (norm1.includes(norm2) || norm2.includes(norm1)) {
    return true;
  }
  
  // Word-by-word matching - if significant words overlap
  const words1 = norm1.split(' ').filter(w => w.length > 2);
  const words2 = norm2.split(' ').filter(w => w.length > 2);
  const overlap = words1.filter(w => words2.includes(w));
  
  // If more than half the words match, consider it a match
  if (overlap.length > 0 && overlap.length >= Math.min(words1.length, words2.length) / 2) {
    return true;
  }
  
  return false;
}

/**
 * Find the best matching PR for an exercise
 */
export function findMatchingPR(
  exerciseName: string, 
  prs: { lift: string; weight: number }[]
): { lift: string; weight: number } | undefined {
  // First try exact canonical match
  const canonical = getCanonicalName(exerciseName);
  
  for (const pr of prs) {
    if (getCanonicalName(pr.lift) === canonical) {
      return pr;
    }
  }
  
  // Then try fuzzy matching
  for (const pr of prs) {
    if (exerciseNamesMatch(exerciseName, pr.lift)) {
      return pr;
    }
  }
  
  return undefined;
}

/**
 * Check if an exercise is a "common PR exercise" that users should track
 */
export function isCommonPRExercise(exerciseName: string): boolean {
  const canonical = getCanonicalName(exerciseName);
  return COMMON_PR_EXERCISES.includes(canonical);
}

/**
 * Get a display-friendly name for a common exercise
 */
export function getDisplayNameForCommonExercise(exerciseName: string): string {
  const canonical = getCanonicalName(exerciseName);
  // Capitalize first letter of each word
  return canonical.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Get an AI-estimated starting weight for an exercise based on user profile
 */
export function getEstimatedWeight(
  exerciseName: string,
  userProfile: { sex?: string; experience?: string; weight?: number }
): number | null {
  const canonical = getCanonicalName(exerciseName);
  const exerciseEstimates = ESTIMATED_WEIGHTS[canonical];
  
  if (!exerciseEstimates) {
    return null; // No estimates for this exercise
  }
  
  // Default to intermediate if no experience specified
  const experience = userProfile.experience?.toLowerCase() || 'intermediate';
  const experienceKey = experience.includes('beginner') ? 'beginner' 
    : experience.includes('advanced') ? 'advanced' 
    : 'intermediate';
  
  const experienceEstimates = exerciseEstimates[experienceKey];
  if (!experienceEstimates) {
    return null;
  }
  
  // Default to male estimates if no sex specified
  const sex = userProfile.sex?.toLowerCase() || 'male';
  const baseWeight = sex === 'female' ? experienceEstimates.female : experienceEstimates.male;
  
  // Optionally adjust based on bodyweight if available
  // This is a simple heuristic - heavier people can typically lift more
  if (userProfile.weight) {
    const avgWeight = sex === 'female' ? 140 : 180;
    const ratio = userProfile.weight / avgWeight;
    // Clamp adjustment to ±30%
    const adjustedRatio = Math.max(0.7, Math.min(1.3, ratio));
    return Math.round((baseWeight * adjustedRatio) / 5) * 5; // Round to nearest 5
  }
  
  return baseWeight;
}

/**
 * Check if we have estimated weights for this exercise type
 */
export function hasEstimatedWeights(exerciseName: string): boolean {
  const canonical = getCanonicalName(exerciseName);
  return canonical in ESTIMATED_WEIGHTS;
}
